import React, {
  FormEvent,
  useMemo,
  useState,
} from "react";

import {
  FontAwesomeIcon,
} from "@fortawesome/react-fontawesome";

import {
  faBell,
  faCalendarDays,
  faCheck,
  faClock,
  faImage,
  faPaperPlane,
  faSave,
  faUsers,
} from "@fortawesome/free-solid-svg-icons";

import type {
  CampaignAccountLevel,
  CampaignAccountType,
  CampaignFrequency,
  CampaignLanguage,
  CampaignPlatform,
  CampaignPriority,
  CampaignSendMode,
  CampaignType,
  CreateNotificationCampaignInput,
  NotificationCampaign,
} from "../../types/notification-campaigns.types";

import "../../screenstyles/NotificationCampaignForm.css";

type Props = {
  initialCampaign?:
    NotificationCampaign |
    null;

  loading?: boolean;

  submitLabel: string;

  onSubmit:
    (
      data:
        CreateNotificationCampaignInput,
    ) =>
      Promise<void> |
      void;
};

const ACCOUNT_TYPES:
  CampaignAccountType[] = [
    "مستخدم",
    "وكيل",
    "منصب",
  ];

const ACCOUNT_LEVELS:
  CampaignAccountLevel[] = [
    "A",
    "B",
    "C",
    "D",
    "E",
    "F",
    "G",
    "H",
    "AgentA",
    "AgentB",
    "AgentC",
    "AgentD",
    "AgentE",
    "AgentF",
    "AgentG",
    "AgentH",
  ];

const LANGUAGES:
  Array<{
    value:
      CampaignLanguage;

    label:
      string;
  }> = [
    {
      value: "ar",
      label:
        "العربية",
    },

    {
      value: "en",
      label:
        "English",
    },

    {
      value: "ku",
      label:
        "کوردی",
    },
  ];

const PLATFORMS:
  Array<{
    value:
      CampaignPlatform;

    label:
      string;
  }> = [
    {
      value: "ios",
      label: "iOS",
    },

    {
      value:
        "android",
      label:
        "Android",
    },

    {
      value: "web",
      label: "Web",
    },
  ];

const DAYS = [
  {
    value: 0,
    label: "الأحد",
  },
  {
    value: 1,
    label:
      "الاثنين",
  },
  {
    value: 2,
    label:
      "الثلاثاء",
  },
  {
    value: 3,
    label:
      "الأربعاء",
  },
  {
    value: 4,
    label:
      "الخميس",
  },
  {
    value: 5,
    label:
      "الجمعة",
  },
  {
    value: 6,
    label:
      "السبت",
  },
];

function timestampToInput(
  value: any,
) {
  if (!value) {
    return "";
  }

  let date:
    Date |
    null = null;

  if (
    typeof value ===
    "string"
  ) {
    date =
      new Date(value);
  } else {
    const seconds =
      value.seconds ??
      value._seconds;

    if (
      typeof seconds ===
      "number"
    ) {
      date =
        new Date(
          seconds *
            1000,
        );
    }
  }

  if (
    !date ||
    Number.isNaN(
      date.getTime(),
    )
  ) {
    return "";
  }

  const offset =
    date.getTimezoneOffset();

  const local =
    new Date(
      date.getTime() -
        offset *
          60 *
          1000,
    );

  return local
    .toISOString()
    .slice(
      0,
      16,
    );
}

function toIso(
  value: string,
) {
  if (!value) {
    return null;
  }

  return new Date(
    value,
  ).toISOString();
}

function toggleValue<T>(
  current: T[],
  value: T,
): T[] {
  return current.includes(
    value,
  )
    ? current.filter(
        item =>
          item !==
          value,
      )
    : [
        ...current,
        value,
      ];
}

const NotificationCampaignForm:
React.FC<Props> = ({
  initialCampaign,
  loading = false,
  submitLabel,
  onSubmit,
}) => {
  const [
    name,
    setName,
  ] =
    useState(
      initialCampaign
        ?.name ??
        "",
    );

  const [
    title,
    setTitle,
  ] =
    useState(
      initialCampaign
        ?.title ??
        "",
    );

  const [
    body,
    setBody,
  ] =
    useState(
      initialCampaign
        ?.body ??
        "",
    );

  const [
    imageUrl,
    setImageUrl,
  ] =
    useState(
      initialCampaign
        ?.imageUrl ??
        "",
    );

  const [
    type,
    setType,
  ] =
    useState<
      CampaignType
    >(
      (
        initialCampaign
          ?.type as
          CampaignType
      ) ??
        "general",
    );

  const [
    priority,
    setPriority,
  ] =
    useState<
      CampaignPriority
    >(
      initialCampaign
        ?.priority ??
        "normal",
    );

  const [
    sendMode,
    setSendMode,
  ] =
    useState<
      CampaignSendMode
    >(
      initialCampaign
        ?.sendMode ??
        "now",
    );

  const [
    enabled,
    setEnabled,
  ] =
    useState(
      initialCampaign
        ?.enabled ??
        true,
    );

  const [
    scheduledAt,
    setScheduledAt,
  ] =
    useState(
      timestampToInput(
        initialCampaign
          ?.scheduledAt,
      ),
    );

  const [
    startAt,
    setStartAt,
  ] =
    useState(
      timestampToInput(
        initialCampaign
          ?.startAt,
      ),
    );

  const [
    endAt,
    setEndAt,
  ] =
    useState(
      timestampToInput(
        initialCampaign
          ?.endAt,
      ),
    );

  const [
    frequency,
    setFrequency,
  ] =
    useState<
      CampaignFrequency
    >(
      initialCampaign
        ?.recurring
        ?.frequency ??
        "daily",
    );

  const [
    hour,
    setHour,
  ] =
    useState(
      initialCampaign
        ?.recurring
        ?.hour ??
        9,
    );

  const [
    minute,
    setMinute,
  ] =
    useState(
      initialCampaign
        ?.recurring
        ?.minute ??
        0,
    );

  const [
    timezone,
    setTimezone,
  ] =
    useState(
      initialCampaign
        ?.recurring
        ?.timezone ??
        "Asia/Baghdad",
    );

  const [
    daysOfWeek,
    setDaysOfWeek,
  ] =
    useState<
      number[]
    >(
      initialCampaign
        ?.recurring
        ?.daysOfWeek ??
        [],
    );

  const [
    dayOfMonth,
    setDayOfMonth,
  ] =
    useState(
      initialCampaign
        ?.recurring
        ?.dayOfMonth ??
        1,
    );

  const [
    accountTypes,
    setAccountTypes,
  ] =
    useState<
      CampaignAccountType[]
    >(
      initialCampaign
        ?.target
        ?.accountTypes ??
        [],
    );

  const [
    accountLevels,
    setAccountLevels,
  ] =
    useState<
      CampaignAccountLevel[]
    >(
      initialCampaign
        ?.target
        ?.accountLevels ??
        [],
    );

  const [
    languages,
    setLanguages,
  ] =
    useState<
      CampaignLanguage[]
    >(
      initialCampaign
        ?.target
        ?.languages ??
        [],
    );

  const [
    platforms,
    setPlatforms,
  ] =
    useState<
      CampaignPlatform[]
    >(
      initialCampaign
        ?.target
        ?.platforms ??
        [],
    );

  const [
    provincesText,
    setProvincesText,
  ] =
    useState(
      initialCampaign
        ?.target
        ?.provinces
        ?.join(", ") ??
        "",
    );

  const [
    userIdsText,
    setUserIdsText,
  ] =
    useState(
      initialCampaign
        ?.target
        ?.userIds
        ?.join("\n") ??
        "",
    );

  const [
    dataJson,
    setDataJson,
  ] =
    useState(
      JSON.stringify(
        initialCampaign
          ?.data ??
          {},
        null,
        2,
      ),
    );

  const preview =
    useMemo(
      () => ({
        title:
          title.trim() ||
          "عنوان الإشعار",

        body:
          body.trim() ||
          "سيظهر نص الإشعار هنا كما سيراه المستخدم في التطبيق.",
      }),
      [
        title,
        body,
      ],
    );

  const handleSubmit =
    async (
      event:
        FormEvent,
    ) => {
      event.preventDefault();

      if (
        !name.trim() ||
        !title.trim() ||
        !body.trim()
      ) {
        window.alert(
          "يرجى إكمال اسم الحملة وعنوان الإشعار ونص الإشعار.",
        );

        return;
      }

      if (
        sendMode ===
          "scheduled" &&
        !scheduledAt
      ) {
        window.alert(
          "يرجى تحديد موعد إرسال الحملة.",
        );

        return;
      }

      if (
        sendMode ===
          "recurring" &&
        frequency ===
          "weekly" &&
        daysOfWeek.length ===
          0
      ) {
        window.alert(
          "حدد يومًا واحدًا على الأقل للحملة الأسبوعية.",
        );

        return;
      }

      let parsedData:
        Record<
          string,
          unknown
        > = {};

      try {
        parsedData =
          dataJson.trim()
            ? JSON.parse(
                dataJson,
              )
            : {};
      } catch {
        window.alert(
          "حقل البيانات الإضافية JSON غير صحيح.",
        );

        return;
      }

      const provinces =
        provincesText
          .split(",")
          .map(
            value =>
              value.trim(),
          )
          .filter(
            Boolean,
          );

      const userIds =
        userIdsText
          .split(
            /\n|,/,
          )
          .map(
            value =>
              value.trim(),
          )
          .filter(
            Boolean,
          );

      const payload:
        CreateNotificationCampaignInput =
        {
          name:
            name.trim(),

          title:
            title.trim(),

          body:
            body.trim(),

          imageUrl:
            imageUrl.trim() ||
            null,

          type,

          priority,

          data:
            parsedData,

          target: {
            accountTypes,

            accountLevels,

            languages,

            platforms,

            provinces,

            userIds,
          },

          sendMode,

          scheduledAt:
            sendMode ===
            "scheduled"
              ? toIso(
                  scheduledAt,
                )
              : null,

          recurring:
            sendMode ===
            "recurring"
              ? {
                  frequency,

                  hour,

                  minute,

                  timezone:
                    timezone.trim() ||
                    "Asia/Baghdad",

                  ...(frequency ===
                  "weekly"
                    ? {
                        daysOfWeek,
                      }
                    : {}),

                  ...(frequency ===
                  "monthly"
                    ? {
                        dayOfMonth,
                      }
                    : {}),
                }
              : null,

          startAt:
            startAt
              ? toIso(
                  startAt,
                )
              : null,

          endAt:
            endAt
              ? toIso(
                  endAt,
                )
              : null,

          enabled,
        };

      await onSubmit(
        payload,
      );
    };

  return (
    <form
      className="notification-campaign-form"
      onSubmit={
        handleSubmit
      }
    >
      <section className="notification-campaign-form-section">
        <div className="notification-campaign-form-section-title">
          <FontAwesomeIcon
            icon={faBell}
          />

          <div>
            <h2>
              بيانات الحملة
            </h2>

            <p>
              اسم الحملة ومحتوى
              الإشعار الذي سيصل
              للمستخدم.
            </p>
          </div>
        </div>

        <div className="notification-campaign-form-grid">
          <div className="notification-campaign-field">
            <label>
              اسم الحملة *
            </label>

            <input
              value={name}
              onChange={
                event =>
                  setName(
                    event
                      .target
                      .value,
                  )
              }
              maxLength={
                150
              }
            />
          </div>

          <div className="notification-campaign-field">
            <label>
              نوع الحملة
            </label>

            <select
              value={type}
              onChange={
                event =>
                  setType(
                    event
                      .target
                      .value as
                      CampaignType,
                  )
              }
            >
              <option value="general">
                عام
              </option>

              <option value="offers">
                عروض
              </option>

              <option value="discounts">
                خصومات
              </option>

              <option value="price_update">
                تحديث أسعار
              </option>

              <option value="points">
                نقاط
              </option>

              <option value="orders">
                طلبات
              </option>

              <option value="products">
                منتجات
              </option>

              <option value="custom">
                مخصص
              </option>
            </select>
          </div>

          <div className="notification-campaign-field">
            <label>
              عنوان الإشعار *
            </label>

            <input
              value={title}
              onChange={
                event =>
                  setTitle(
                    event
                      .target
                      .value,
                  )
              }
              maxLength={
                200
              }
            />
          </div>

          <div className="notification-campaign-field">
            <label>
              الأولوية
            </label>

            <select
              value={
                priority
              }
              onChange={
                event =>
                  setPriority(
                    event
                      .target
                      .value as
                      CampaignPriority,
                  )
              }
            >
              <option value="low">
                منخفضة
              </option>

              <option value="normal">
                عادية
              </option>

              <option value="high">
                عالية
              </option>
            </select>
          </div>

          <div className="notification-campaign-field notification-campaign-field-full">
            <label>
              نص الإشعار *
            </label>

            <textarea
              value={body}
              onChange={
                event =>
                  setBody(
                    event
                      .target
                      .value,
                  )
              }
              maxLength={
                1000
              }
              rows={5}
            />

            <small>
              {body.length}
              /1000
            </small>
          </div>

          <div className="notification-campaign-field notification-campaign-field-full">
            <label>
              رابط صورة الإشعار
            </label>

            <div className="notification-campaign-input-icon">
              <FontAwesomeIcon
                icon={
                  faImage
                }
              />

              <input
                type="url"
                value={
                  imageUrl
                }
                onChange={
                  event =>
                    setImageUrl(
                      event
                        .target
                        .value,
                    )
                }
                placeholder="https://..."
                dir="ltr"
              />
            </div>
          </div>
        </div>

        <div className="notification-preview">
          <div className="notification-preview-icon">
            <FontAwesomeIcon
              icon={faBell}
            />
          </div>

          <div>
            <strong>
              {preview.title}
            </strong>

            <p>
              {preview.body}
            </p>
          </div>

          {imageUrl && (
            <img
              src={
                imageUrl
              }
              alt=""
            />
          )}
        </div>
      </section>

      <section className="notification-campaign-form-section">
        <div className="notification-campaign-form-section-title">
          <FontAwesomeIcon
            icon={faUsers}
          />

          <div>
            <h2>
              الجمهور المستهدف
            </h2>

            <p>
              ترك الحقول فارغة
              يعني عدم تقييد الحملة
              بهذا الفلتر.
            </p>
          </div>
        </div>

        <h3 className="notification-campaign-selection-title">
          نوع الحساب
        </h3>

        <div className="notification-campaign-selection-grid">
          {ACCOUNT_TYPES.map(
            value => {
              const checked =
                accountTypes.includes(
                  value,
                );

              return (
                <button
                  type="button"
                  key={
                    value
                  }
                  className={
                    checked
                      ? "notification-campaign-selection selected"
                      : "notification-campaign-selection"
                  }
                  onClick={() =>
                    setAccountTypes(
                      previous =>
                        toggleValue(
                          previous,
                          value,
                        ),
                    )
                  }
                >
                  <span>
                    {checked && (
                      <FontAwesomeIcon
                        icon={
                          faCheck
                        }
                      />
                    )}
                  </span>

                  {value}
                </button>
              );
            },
          )}
        </div>

        <h3 className="notification-campaign-selection-title">
          مستوى الحساب
        </h3>

        <div className="notification-campaign-selection-grid">
          {ACCOUNT_LEVELS.map(
            value => {
              const checked =
                accountLevels.includes(
                  value,
                );

              return (
                <button
                  type="button"
                  key={
                    value
                  }
                  className={
                    checked
                      ? "notification-campaign-selection selected"
                      : "notification-campaign-selection"
                  }
                  onClick={() =>
                    setAccountLevels(
                      previous =>
                        toggleValue(
                          previous,
                          value,
                        ),
                    )
                  }
                >
                  <span>
                    {checked && (
                      <FontAwesomeIcon
                        icon={
                          faCheck
                        }
                      />
                    )}
                  </span>

                  {value}
                </button>
              );
            },
          )}
        </div>

        <h3 className="notification-campaign-selection-title">
          اللغة
        </h3>

        <div className="notification-campaign-selection-grid">
          {LANGUAGES.map(
            item => {
              const checked =
                languages.includes(
                  item.value,
                );

              return (
                <button
                  type="button"
                  key={
                    item.value
                  }
                  className={
                    checked
                      ? "notification-campaign-selection selected"
                      : "notification-campaign-selection"
                  }
                  onClick={() =>
                    setLanguages(
                      previous =>
                        toggleValue(
                          previous,
                          item.value,
                        ),
                    )
                  }
                >
                  <span>
                    {checked && (
                      <FontAwesomeIcon
                        icon={
                          faCheck
                        }
                      />
                    )}
                  </span>

                  {
                    item.label
                  }
                </button>
              );
            },
          )}
        </div>

        <h3 className="notification-campaign-selection-title">
          المنصة
        </h3>

        <div className="notification-campaign-selection-grid">
          {PLATFORMS.map(
            item => {
              const checked =
                platforms.includes(
                  item.value,
                );

              return (
                <button
                  type="button"
                  key={
                    item.value
                  }
                  className={
                    checked
                      ? "notification-campaign-selection selected"
                      : "notification-campaign-selection"
                  }
                  onClick={() =>
                    setPlatforms(
                      previous =>
                        toggleValue(
                          previous,
                          item.value,
                        ),
                    )
                  }
                >
                  <span>
                    {checked && (
                      <FontAwesomeIcon
                        icon={
                          faCheck
                        }
                      />
                    )}
                  </span>

                  {
                    item.label
                  }
                </button>
              );
            },
          )}
        </div>

        <div className="notification-campaign-form-grid notification-campaign-form-target-grid">
          <div className="notification-campaign-field">
            <label>
              المحافظات
            </label>

            <textarea
              rows={4}
              value={
                provincesText
              }
              onChange={
                event =>
                  setProvincesText(
                    event
                      .target
                      .value,
                  )
              }
              placeholder="بغداد, بابل, النجف"
            />

            <small>
              افصل المحافظات
              بفاصلة.
            </small>
          </div>

          <div className="notification-campaign-field">
            <label>
              User IDs
            </label>

            <textarea
              rows={4}
              value={
                userIdsText
              }
              onChange={
                event =>
                  setUserIdsText(
                    event
                      .target
                      .value,
                  )
              }
              placeholder="UID لكل سطر"
              dir="ltr"
            />

            <small>
              استخدمه عند إرسال
              الحملة لمستخدمين
              محددين.
            </small>
          </div>
        </div>
      </section>

      <section className="notification-campaign-form-section">
        <div className="notification-campaign-form-section-title">
          <FontAwesomeIcon
            icon={
              faCalendarDays
            }
          />

          <div>
            <h2>
              توقيت الإرسال
            </h2>

            <p>
              إرسال مباشر أو مجدول
              أو متكرر.
            </p>
          </div>
        </div>

        <div className="notification-send-mode-grid">
          {(
            [
              {
                value:
                  "now",
                label:
                  "إرسال مباشر",
                icon:
                  faPaperPlane,
              },

              {
                value:
                  "scheduled",
                label:
                  "موعد محدد",
                icon:
                  faCalendarDays,
              },

              {
                value:
                  "recurring",
                label:
                  "إرسال متكرر",
                icon:
                  faClock,
              },
            ] as const
          ).map(
            item => (
              <button
                type="button"
                key={
                  item.value
                }
                className={
                  sendMode ===
                  item.value
                    ? "notification-send-mode selected"
                    : "notification-send-mode"
                }
                onClick={() =>
                  setSendMode(
                    item.value,
                  )
                }
              >
                <FontAwesomeIcon
                  icon={
                    item.icon
                  }
                />

                {
                  item.label
                }
              </button>
            ),
          )}
        </div>

        {sendMode ===
          "scheduled" && (
          <div className="notification-campaign-form-grid">
            <div className="notification-campaign-field">
              <label>
                موعد الإرسال *
              </label>

              <input
                type="datetime-local"
                value={
                  scheduledAt
                }
                onChange={
                  event =>
                    setScheduledAt(
                      event
                        .target
                        .value,
                    )
                }
              />
            </div>
          </div>
        )}

        {sendMode ===
          "recurring" && (
          <div className="notification-recurring-box">
            <div className="notification-campaign-form-grid">
              <div className="notification-campaign-field">
                <label>
                  التكرار
                </label>

                <select
                  value={
                    frequency
                  }
                  onChange={
                    event =>
                      setFrequency(
                        event
                          .target
                          .value as
                          CampaignFrequency,
                      )
                  }
                >
                  <option value="daily">
                    يوميًا
                  </option>

                  <option value="weekly">
                    أسبوعيًا
                  </option>

                  <option value="monthly">
                    شهريًا
                  </option>
                </select>
              </div>

              <div className="notification-campaign-field">
                <label>
                  الساعة
                </label>

                <input
                  type="number"
                  min={0}
                  max={23}
                  value={
                    hour
                  }
                  onChange={
                    event =>
                      setHour(
                        Number(
                          event
                            .target
                            .value,
                        ),
                      )
                  }
                />
              </div>

              <div className="notification-campaign-field">
                <label>
                  الدقيقة
                </label>

                <input
                  type="number"
                  min={0}
                  max={59}
                  value={
                    minute
                  }
                  onChange={
                    event =>
                      setMinute(
                        Number(
                          event
                            .target
                            .value,
                        ),
                      )
                  }
                />
              </div>

              <div className="notification-campaign-field">
                <label>
                  المنطقة الزمنية
                </label>

                <input
                  value={
                    timezone
                  }
                  onChange={
                    event =>
                      setTimezone(
                        event
                          .target
                          .value,
                      )
                  }
                  dir="ltr"
                />
              </div>
            </div>

            {frequency ===
              "weekly" && (
              <>
                <h3 className="notification-campaign-selection-title">
                  أيام الأسبوع
                </h3>

                <div className="notification-campaign-selection-grid">
                  {DAYS.map(
                    day => {
                      const checked =
                        daysOfWeek.includes(
                          day.value,
                        );

                      return (
                        <button
                          type="button"
                          key={
                            day.value
                          }
                          className={
                            checked
                              ? "notification-campaign-selection selected"
                              : "notification-campaign-selection"
                          }
                          onClick={() =>
                            setDaysOfWeek(
                              previous =>
                                toggleValue(
                                  previous,
                                  day.value,
                                ),
                            )
                          }
                        >
                          {
                            day.label
                          }
                        </button>
                      );
                    },
                  )}
                </div>
              </>
            )}

            {frequency ===
              "monthly" && (
              <div className="notification-campaign-field notification-month-day">
                <label>
                  يوم الشهر
                </label>

                <input
                  type="number"
                  min={1}
                  max={31}
                  value={
                    dayOfMonth
                  }
                  onChange={
                    event =>
                      setDayOfMonth(
                        Number(
                          event
                            .target
                            .value,
                        ),
                      )
                  }
                />
              </div>
            )}
          </div>
        )}

        <div className="notification-campaign-form-grid notification-campaign-window-grid">
          <div className="notification-campaign-field">
            <label>
              بداية فترة الحملة
            </label>

            <input
              type="datetime-local"
              value={
                startAt
              }
              onChange={
                event =>
                  setStartAt(
                    event
                      .target
                      .value,
                  )
              }
            />
          </div>

          <div className="notification-campaign-field">
            <label>
              نهاية فترة الحملة
            </label>

            <input
              type="datetime-local"
              value={
                endAt
              }
              onChange={
                event =>
                  setEndAt(
                    event
                      .target
                      .value,
                  )
              }
            />
          </div>
        </div>
      </section>

      <section className="notification-campaign-form-section">
        <div className="notification-campaign-form-section-title">
          <FontAwesomeIcon
            icon={faSave}
          />

          <div>
            <h2>
              خيارات إضافية
            </h2>

            <p>
              بيانات إضافية وحالة
              تفعيل الحملة.
            </p>
          </div>
        </div>

        <label className="notification-campaign-switch">
          <div>
            <strong>
              الحملة مفعلة
            </strong>

            <span>
              تعطيل هذا الخيار
              يمنع تشغيل الحملة.
            </span>
          </div>

          <input
            type="checkbox"
            checked={
              enabled
            }
            onChange={
              event =>
                setEnabled(
                  event
                    .target
                    .checked,
                )
            }
          />
        </label>

        <div className="notification-campaign-field notification-campaign-json-field">
          <label>
            بيانات إضافية JSON
          </label>

          <textarea
            value={
              dataJson
            }
            onChange={
              event =>
                setDataJson(
                  event
                    .target
                    .value,
                )
            }
            rows={8}
            dir="ltr"
            spellCheck={
              false
            }
          />
        </div>
      </section>

      <div className="notification-campaign-form-actions">
        <button
          type="submit"
          className="notification-campaign-submit-btn"
          disabled={
            loading
          }
        >
          <FontAwesomeIcon
            icon={faSave}
          />

          {loading
            ? "جاري الحفظ..."
            : submitLabel}
        </button>
      </div>
    </form>
  );
};

export default NotificationCampaignForm;